/**
 * A coverage window is an offset-and-length, not a timeline item — "the
 * photographer is booked 11:00 AM to 8:00 PM," which overlaps the ceremony,
 * cocktail hour, and however much of dinner the planner wants shot. It takes
 * no part in lane assignment, drags, or neighbour arithmetic: those exist to
 * lay timeline items out against each other on an axis, and a window is not
 * laid out against anything, it is just read.
 *
 * `durationMinutes` here is a plain number, with no null case. A timeline
 * item's duration may be null because the item can run to whatever comes
 * next, or to the end of the night if it is last — there is always something
 * downstream to borrow a length from. A coverage window has no "next window"
 * in any chain; nothing else supplies its length if the window itself does
 * not state one, so the field that is optional-nullable on a timeline item is
 * required here. See `shared/schemas/coverage.ts` for where that is enforced
 * on the way in.
 */
import {
  formatTimeValue,
  formatWeddingHours,
  minutesSinceMidnight,
  parseTimeOfDay,
  timeOfDayFromMinutes,
} from './wedding-hours';

export interface CoverageAnnotation {
  id: string;
  /** The vendor category this window resolves `{{coverage_hours}}` for, or null for free text. */
  category: string | null;
  /** A free-text window's own label, or null when `category` is set instead. */
  label: string | null;
  offsetMinutes: number;
  durationMinutes: number;
}

/**
 * The caller's own `T`, with the wall-clock reading added onto it — not
 * wrapped around it. Callers that attach extra fields (a frontend row id, a
 * drag-handle flag) get those back untouched on the same object, with
 * `clockTime` and friends sitting alongside rather than one level down.
 */
export type ResolvedCoverageAnnotation<T extends CoverageAnnotation> = T & {
  /** `'HH:MM'` wall-clock at the venue. */
  clockTime: string;
  /** `'HH:MM'` wall-clock the window closes at. */
  endClockTime: string;
  /** Calendar days from the wedding's own date for `clockTime`. */
  dayOffset: number;
  /** Calendar days from the wedding's own date for `endClockTime`. */
  endDayOffset: number;
  /** `'11:00 AM – 8:00 PM'` — what a merge variable or the timeline reads back. */
  hours: string;
};

/**
 * Offsets in, the strings an email carries out.
 *
 * Modelled directly on `resolveTimeline` in `./timeline`, and it shares that
 * function's refusal: an offset with nothing to be an offset from is not a
 * time, so a missing or unparseable `startTime` returns `[]` rather than
 * guessing an anchor and filing the window at a moment the planner never
 * named while reporting success. The UI's job on an empty result is to ask
 * for the wedding's start time, same as it does for the timeline.
 *
 * `endTime` is accepted, not used, and kept only for call-site symmetry with
 * `resolveTimeline` — the route resolving either one passes the wedding's
 * hours as the same pair. A timeline item's open-ended duration needs the
 * end to know how long "runs to the end of the night" is; a coverage window
 * always states its own length, so it needs no span to resolve one. A window
 * that runs past the end of the night is therefore ordinary here, not the
 * collision `endsAfterWedding` exists to flag on a timeline item — nothing
 * downstream of a vendor's own booked hours cares whether the reception
 * technically ended first.
 */
export function resolveCoverage<T extends CoverageAnnotation>(
  annotations: readonly T[],
  startTime: string | null | undefined,
  // Leading underscore: kept for call-site symmetry with `resolveTimeline`
  // (see above) but genuinely unread, and `noUnusedParameters` would
  // otherwise refuse the file for it.
  _endTime?: string | null,
): ResolvedCoverageAnnotation<T>[] {
  const start = startTime ? parseTimeOfDay(startTime) : null;
  if (!start) return [];

  const startMinutes = minutesSinceMidnight(start);

  // Sorts a copy — never the caller's own array — by when each window opens,
  // the same ordering `resolveTimeline` gives timeline items for the same
  // reason: a list read top to bottom should read as the day runs.
  const sorted = [...annotations].sort((a, b) => a.offsetMinutes - b.offsetMinutes);

  return sorted.map((annotation) => {
    const absoluteStart = startMinutes + annotation.offsetMinutes;
    const absoluteEnd = absoluteStart + annotation.durationMinutes;

    const clockTime = formatTimeValue(timeOfDayFromMinutes(absoluteStart));
    const endClockTime = formatTimeValue(timeOfDayFromMinutes(absoluteEnd));

    // formatWeddingHours only returns null when its start argument fails to
    // parse as a time of day, and clockTime was just produced by
    // formatTimeValue, which emits exactly the 'HH:MM' shape formatWeddingHours
    // parses — so this can never actually be null.
    const hours = formatWeddingHours(clockTime, endClockTime)!;

    return {
      ...annotation,
      clockTime,
      endClockTime,
      // Floor, not truncation: a -90 absolute is the previous day, and
      // `Math.trunc` would call it day zero. Same bug class `resolveTimeline`
      // guards against with the same comment.
      dayOffset: Math.floor(absoluteStart / 1440),
      endDayOffset: Math.floor(absoluteEnd / 1440),
      hours,
    };
  });
}

/**
 * How a category's windows read as one phrase.
 *
 * `"11:00 AM – 2:00 PM and 5:00 PM – 8:00 PM"`, and the joining is the whole
 * reason this exists. A vendor can be booked in more than one stretch — a
 * florist who sets up, leaves while the ceremony runs, and comes back for the
 * reception is the ordinary case, not an exotic one — and the alternative to
 * joining was to make the planner record that as a single window from the first
 * arrival to the last departure. That window would be a lie told to the vendor
 * in their own inquiry: it says they are booked for hours they are not.
 *
 * Serial `and` rather than a bare comma list, because this is dropped mid
 * sentence into prose a planner wrote — "I have you booked {{coverage_hours}}"
 * — and a comma list reads there as an unfinished thought.
 */
function joinHours(hours: readonly string[]): string {
  if (hours.length <= 1) return hours[0] ?? '';
  return `${hours.slice(0, -1).join(', ')} and ${hours[hours.length - 1]}`;
}

/**
 * Category name to that category's hours — the lookup `{{coverage_hours}}`
 * merges against, keyed by the recipient vendor's own category so a
 * photographer reads their windows and a florist reads theirs.
 *
 * Every window a category has, joined in the order they run. `resolveCoverage`
 * sorts by offset, so passing its output through here gives the day in the
 * order it happens — which is also the order a vendor reading the sentence
 * expects, and the reason this takes the resolved rows rather than the stored
 * ones.
 *
 * Free-text windows (`category` null) are left out, and that is the rule, not
 * an optimization worth second-guessing. A window labelled "Venue access"
 * names a constraint, not a vendor — nothing in the system can match that
 * string to a recipient, so it must not sit in a map that looks like it
 * resolves one. Silently including it under some placeholder key would make
 * a free-text note resolvable by accident the first time a category happened
 * to collide with it.
 */
export function coverageByCategory(
  annotations: readonly Pick<ResolvedCoverageAnnotation<CoverageAnnotation>, 'category' | 'hours'>[],
): Map<string, string> {
  const byCategory = new Map<string, string[]>();
  for (const { category, hours } of annotations) {
    if (category === null) continue;
    const held = byCategory.get(category);
    if (held) held.push(hours);
    else byCategory.set(category, [hours]);
  }

  return new Map([...byCategory].map(([category, hours]) => [category, joinHours(hours)]));
}

/**
 * How long a window runs, given the clock time a planner typed as its end.
 *
 * The counterpart to `offsetForClockTime`, and deliberately not the same rule.
 * A *start* time is genuinely ambiguous against a wedding's own hours — 2:00 PM
 * against a 5:30 PM start could be the afternoon before or the small hours
 * after — so that function settles it with a documented cutoff, and the cutoff
 * is a guess that is usually right.
 *
 * An end time needs no guess. It has to fall after the start it belongs to,
 * which leaves exactly one reading: the next time the clock says so. A
 * photographer who finishes at 1:00 AM finishes the following morning whatever
 * hour they began, and a window starting at 8:00 PM and ending at 10:00 AM is
 * fourteen hours rather than minus ten. The modulo *is* the rule here, not a
 * wrap being papered over.
 *
 * Zero means the planner typed the start time into the end field. Returned
 * honestly rather than rounded up to a whole day, because a day is not a
 * plausible repair of that mistake; `MIN_WINDOW_MINUTES` is what refuses it.
 *
 * Null when there is no start to measure from, or when the time cannot be read
 * at all — the same two holes every function in this family has.
 */
export function coverageDurationForEnd(
  startTime: string | null | undefined,
  offsetMinutes: number,
  endClockTime: string,
): number | null {
  const start = startTime ? parseTimeOfDay(startTime) : null;
  const end = parseTimeOfDay(endClockTime);
  if (!start || !end) return null;
  // What the clock reads at the window's own start, which is the minute the
  // end has to be measured forward from — not the wedding's start.
  const from = minutesSinceMidnight(start) + offsetMinutes;
  const diff = minutesSinceMidnight(end) - from;
  // `((d % n) + n) % n` rather than `d % n`: the remainder of a negative
  // number is negative in JS, and a negative length is the one answer this
  // cannot give.
  return ((diff % 1440) + 1440) % 1440;
}
